<template>
  <div v-if="!item.hidden">
    <e-tooltip location="bottom center" max-width="240" :disabled="!isPermissionsDisabled">
      <template #activator>
        <component
          v-if="!item.children"
          :is="tag"
          :to="item.to"
          :title="item.title"
          :class="{ disabled: props.disabled || isPermissionsDisabled, active: item.active }"
          :id="item.id"
          class="menu-item"
          @click="onClick"
        >
          <e-icon
            v-if="item.icon || item.lockedIcon"
            class="menu-item__icon"
            :name="isPermissionsDisabled ? item.lockedIcon || item.icon : item.icon"
          />
          <span v-if="item.withBadge" class="menu-item__badge" />
          {{ item.title }}
        </component>
        <v-menu
          v-else
          :open-on-hover="false"
          :disabled="props.disabled || !showChildrenInTooltip"
          min-width="150px"
          location="center"
          content-class="menu-item__popper-content"
        >
          <template #activator="{ props: activatorProps }">
            <v-expansion-panels
              v-model="expandedValue"
              :id="item.id"
              :disabled="props.disabled || isPermissionsDisabled"
              :readonly="showChildrenInTooltip"
              v-bind="activatorProps"
            >
              <v-expansion-panel
                :disabled="disabled"
                static
                collapse-icon=""
                expand-icon=""
                class="menu-item__expansion-panel"
              >
                <template #title>
                  <div
                    class="menu-item"
                    :class="{
                      disabled: props.disabled || isPermissionsDisabled,
                      active: item.active || (!isDefined(expandedValue) && item.children.some((c) => c.active)),
                    }"
                  >
                    <e-icon
                      class="menu-item__icon"
                      :name="isPermissionsDisabled ? item.lockedIcon || item.icon : item.icon"
                    />
                    <span v-if="item.withBadge" class="menu-item__badge" />
                    {{ item.title }}
                    <e-icon name="arrowDropdown" :size="10" class="ml-3 menu-item__icon-arrow" />
                  </div>
                </template>
                <template #text>
                  <router-link
                    v-for="(children, index) in item.children"
                    :key="index"
                    :to="children.to"
                    :title="children.title"
                    :class="{ active: children.active }"
                    class="menu-item"
                    @click="onClick"
                  >
                    {{ children.title }}
                  </router-link>
                </template>
              </v-expansion-panel>
            </v-expansion-panels>
          </template>
          <e-btn
            v-for="(children, index) in item.children"
            :key="index"
            :to="children.to"
            :title="children.title"
            variant="context"
            size="M"
            class="menu-item"
          >
            {{ children.title }}
          </e-btn>
        </v-menu>
      </template>
      <div v-if="isPermissionsDisabled" class="text-secondary">
        <div class="paragraph-s font-600">Доступ закрыт</div>
        <div class="paragraph-xs mt-2">Недостаточно прав для просмотра раздела</div>
      </div>
    </e-tooltip>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, toRef, watch } from 'vue';
import { VMenu } from 'vuetify/components';
import type { MenuItemType } from '@/core/types/components/components';
import { EBtn, EIcon, ETooltip } from '@/core/components/ui';
import { useLayoutStore } from '@/core/store/layout';
import { isDefined } from '@/core/utils/common';
import { useAuthStore } from '@/parts/auth/store/auth';

const props = defineProps<{
  item: MenuItemType;
  disabled?: boolean;
}>();

const emit = defineEmits<{
  (event: 'click'): void;
}>();

const expandedValue = ref<number>();

const { isCollapsedNavigation } = useLayoutStore();
const { profile } = useAuthStore();

watch(isCollapsedNavigation, (value) => {
  if (value) {
    expandedValue.value = undefined;
  }
});

const tag = toRef(() => (props.item.to ? 'router-link' : 'div'));

const showChildrenInTooltip = toRef(() => !!(isCollapsedNavigation.value && props.item.children));

const onClick = () => {
  if (props.disabled) {
    return;
  }

  emit('click');

  void props.item.action?.();
};

const isPermissionsDisabled = computed(() => {
  if (props.item.disabledIfRoleNotAssigned && !profile.value?.permissions.length) {
    return true;
  }

  if (props.item.permissions?.length) {
    return !props.item.permissions.some((permissionsItem) => profile.value?.permissions.includes(permissionsItem));
  }

  return false;
});
</script>

<style scoped lang="scss">
$parent: menu-item;
.#{$parent} {
  position: relative;

  display: flex;
  align-items: center;

  height: 44px;
  margin-bottom: 4px;
  padding-left: 10px;

  font-size: $text-font-size;
  font-weight: 500;

  cursor: pointer;

  @include lg-and-down {
    padding-left: 8px;
  }

  @include xs {
    padding-left: 10px;
  }

  &.disabled {
    color: $text-inactive-color;

    pointer-events: none;

    .#{$parent}__icon {
      color: $text-inactive-color;
    }
  }

  &:before {
    content: '';
    position: absolute;
    top: 50%;
    left: -24px;

    width: 5px;
    height: 100%;

    background-color: $primary-color;
    border-radius: 0 $border-radius-s $border-radius-s 0;

    transform: translateY(-50%) translateX(-100%);
    transition: transform $transition-duration;

    @include xs {
      left: -16px;
    }
  }

  &.router-link-active,
  &.active {
    color: $primary-color;

    .#{$parent}__icon {
      color: $primary-color;
    }

    &:before {
      transform: translateY(-50%);
    }
  }

  @include hover {
    color: $primary-color;

    .#{$parent}__icon {
      color: $primary-color;
    }
  }

  &__icon {
    margin-right: 11px;

    color: $text-secondary-color;
  }

  &__badge {
    width: 8px;
    height: 8px;
    margin: -20px 8px 0 -16px;

    background-color: $alert-color;
    border-radius: 50%;
  }

  &__expansion-panel {
    background-color: transparent;

    .#{$parent}__icon-arrow {
      transition: transform $transition-duration;
    }

    &.v-expansion-panel--active {
      .#{$parent}__icon-arrow {
        transform: rotate(180deg);
      }
    }

    :deep(.v-expansion-panel-title) {
      padding: 0;

      @include hover {
        color: $primary-color;

        .#{$parent}__icon,
        .#{$parent}__icon-arrow {
          color: $primary-color;
        }
      }
    }

    :deep(.v-expansion-panel-text__wrapper) {
      padding: 0;

      .#{$parent} {
        padding-left: 45px;
      }
    }

    :deep(.v-expansion-panel-title__overlay) {
      display: none;
    }

    :deep(.v-expansion-panel__shadow) {
      display: none;
    }
  }
}

:deep(.#{$parent}__popper-content) {
  padding: 8px;

  background-color: $white-color;
  border-radius: $border-radius-l;
  box-shadow: $box-shadow;

  transform: translate(36px, calc(50% - 28px));

  &:before {
    content: '';
    position: absolute;
    top: 20px;
    left: -15px;

    width: 12px;
    height: 12px;

    background: url('@/assets/img/layouts/chevron-arrow.svg') no-repeat center center / contain;
  }

  a {
    &:before {
      display: none;
    }
  }
}
</style>
